import React, { useState, useEffect } from "react";
import commonFunc from "src/utils/commonFunc";

import { useHistory } from "react-router-dom";

import {
  Table,
  Card,
  Modal,
  Button,
  Space,
  Input,
  message,
  Radio,
  Spin,
} from "antd";

import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FieldNumberOutlined,
  CalendarOutlined,
  LeftOutlined,
} from "@ant-design/icons";

import "./index.css";

const { formatDate } = commonFunc;

export default ({ shifts, cancelShift, profile }) => {
  let history = useHistory();
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [filter, setFilter] = useState("all");
  const [dataSource, setDataSource] = useState([]);

  const comeback = () => {
    history.push("/processing");
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilter(value);
  };

  const filterShifts = (filter, shifts, searchValue) => {
    let filteredData = shifts || [];

    const doesWorkerMatch = (worker, searchValueLower, searchNumber) => {
      return (
        worker.firstname?.toLowerCase().includes(searchValueLower) ||
        worker.lastname?.toLowerCase().includes(searchValueLower) ||
        worker.additionalName?.toLowerCase().includes(searchValueLower) ||
        worker.serial === searchNumber ||
        worker.totalWeight === searchNumber
      );
    };

    if (searchValue) {
      const searchValueLower = searchValue.toLowerCase();
      const searchNumber = parseFloat(searchValue);

      filteredData = filteredData.filter((shift) => {
        return Object.values(shift.workers).some((worker) =>
          doesWorkerMatch(worker, searchValueLower, searchNumber)
        );
      });
    }

    switch (filter) {
      case "dayShift":
        return filteredData.filter(
          (rO) => rO.period === "day" && !rO.cancelled
        );
      case "nightShift":
        return filteredData.filter(
          (rO) => rO.period === "night" && !rO.cancelled
        );
      case "withRepair":
        return filteredData.filter((rO) => rO.repairTime > 0 && !rO.cancelled);
      case "cancelled":
        return filteredData.filter((rO) => rO.cancelled);
      default:
        return filteredData;
    }
  };

  const prepareName = (worker) => {
    if (!worker) {
      return "";
    }

    const { firstname = "", lastname = "", additionalName = "" } = worker;

    return `${firstname} ${lastname} ${additionalName}`.trim();
  };

  const prepareDataSource = (data) => {
    const sortedData = [...data].sort((a, b) => b.serial - a.serial);

    return sortedData.map((shift) => ({
      ...shift,
      key: shift._id,
      date: formatDate(shift.date).slice(0, 10),
      senior: prepareName(shift.workers.senior),
      junior1: prepareName(shift.workers.junior1),
      junior2: prepareName(shift.workers.junior2),
      junior3: prepareName(shift.workers.junior3),
      junior4: prepareName(shift.workers.junior4),
      junior5: prepareName(shift.workers.junior5),
      junior6: prepareName(shift.workers.junior6),
      porter1: prepareName(shift.workers.porter1),
      porter2: prepareName(shift.workers.porter2),
      porter3: prepareName(shift.workers.porter3),
      porter4: prepareName(shift.workers.porter4),
      porter5: prepareName(shift.workers.porter5),
      porter6: prepareName(shift.workers.porter6),
      loader: prepareName(shift.workers.loader),
      helperOfTechnologist: prepareName(shift.workers.helperOfTechnologist),
      grainstoreWorker1: prepareName(shift.workers.grainstoreWorker1),
      grainstoreWorker2: prepareName(shift.workers.grainstoreWorker2),
      packer: prepareName(shift.workers.packer),
      instances: shift.instances.map((instance, idx) => ({
        ...instance,
        percent: (instance.weight / shift.totalWeight) * 100,
        key: `${shift._id}-${idx}`,
      })),
    }));
  };

  const handleEditShift = async (shift) => {
    console.log(shift, "shift");
    history.push({
      pathname: `/processing/shift/edit`,
      state: {
        shift,
        listViewState: { filter, isModalVisible, searchValue },
        from: history.location.pathname,
      },
    });
  };

  const handleCancelShift = async (values) => {
    setLoading(true);
    values.creatorId = profile._id;
    var result = await cancelShift(values);

    if (result.success) {
      message.success("Shift successfully cancelled!");
    } else {
      message.error(result.message || "Failed to cancel!");
      setLoading(false);
    }
  };

  const showConfirmCancel = (shift) => {
    Modal.confirm({
      title: "Are you sure you want to cancel this shift?",
      icon: <ExclamationCircleOutlined />,
      content: "This action cannot be undone.",
      okText: "Yes, cancel",
      okType: "danger",
      cancelText: "Cancel",
      onOk() {
        handleCancelShift(shift);
      },
      onCancel() {
        console.log("Cancelled");
      },
    });
  };

  const showModal = (shift) => {
    setSelectedShift(shift);
    setIsModalVisible(true);
  };

  const columnsForModal = [
    {
      title: <span className="column-title-font">Product</span>,
      dataIndex: "title",
      key: "title",
      render: (text) => <span className="column-content-font">{text}</span>,
    },
    {
      title: <span className="column-title-font">Produced:</span>,
      dataIndex: "weight",
      key: "weight",
      render: (text) => <span className="column-content-font">{text} kg</span>,
    },
    {
      title: <span className="column-title-font">Percentage:</span>,
      dataIndex: "percent",
      key: "percent",
      render: (text) => (
        <span className="column-content-font">{text.toFixed(1)} %</span>
      ),
    },
  ];

  const ShiftModalContent = () => {
    if (!selectedShift) {
      return null;
    }

    const {
      date,
      serial,
      senior,
      junior1,
      junior2,
      junior3,
      junior4,
      junior5,
      junior6,
      porter1,
      porter2,
      porter3,
      porter4,
      porter5,
      porter6,
      loader,
      helperOfTechnologist,
      grainstoreWorker1,
      grainstoreWorker2,
      packer,
      instances,
      totalWeight,
      comment,
      cancelled,
      period,
      workers,
      repairTime,
      type,
    } = selectedShift;

    return (
      <Card
        size="small"
        title={
          type !== "initialProducts"
            ? `Shift №${serial}`
            : "Initial Balance Entry"
        }
        extra={
          <span className="column-title-font">
            Date: {cancelled ? "CANCELLED" : date.slice(0, 10)} -{" "}
            {type !== "initialProducts"
              ? period === "day"
                ? "day shift"
                : " night shift"
              : "initial balance entry"}
          </span>
        }
      >
        {type !== "initialProducts" && (
          <div className="common-modal__info-block">
            <span className="common-info-title">Repair:</span>
            <div>
              <span className="column-title-font">Hours spent:</span>
              <span className="column-content-font">{repairTime}</span>
            </div>
            <span className="common-info-title">Shift workers:</span>
            <div>
              <span className="column-title-font">Senior:</span>
              <span className="column-content-font">{senior} / </span>
              <span className="column-content-font" style={{ color: "green" }}>
                {" "}
                {workers.senior.profit} rub.
              </span>
            </div>
            <div>
              <span className="column-title-font">Junior №1:</span>
              <span className="column-content-font">{junior1} /</span>
              <span className="column-content-font" style={{ color: "green" }}>
                {" "}
                {workers.junior1.profit} rub.
              </span>
            </div>
            <div>
              <span className="column-title-font">Junior №2:</span>
              <span className="column-content-font">{junior2} /</span>
              <span className="column-content-font" style={{ color: "green" }}>
                {" "}
                {workers.junior2.profit} rub.
              </span>
            </div>
            {junior3 && (
              <div>
                <span className="column-title-font">Junior №3:</span>
                <span className="column-content-font">{junior3} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.junior3.profit} rub.
                </span>
              </div>
            )}
            {junior4 && (
              <div>
                <span className="column-title-font">Junior №4:</span>
                <span className="column-content-font">{junior4} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.junior4.profit} rub.
                </span>
              </div>
            )}
            {junior5 && (
              <div>
                <span className="column-title-font">Junior №5:</span>
                <span className="column-content-font">{junior5} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.junior5.profit} rub.
                </span>
              </div>
            )}
            {junior6 && (
              <div>
                <span className="column-title-font">Junior №6:</span>
                <span className="column-content-font">{junior6} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.junior6.profit} rub.
                </span>
              </div>
            )}
            {porter1 && (
              <div>
                <span className="column-title-font">Porter №1:</span>
                <span className="column-content-font">{porter1} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.porter1.profit} rub.
                </span>
              </div>
            )}
            {porter2 && (
              <div>
                <span className="column-title-font">Porter №2:</span>
                <span className="column-content-font">{porter2} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.porter2.profit} rub.
                </span>
              </div>
            )}
            {porter3 && (
              <div>
                <span className="column-title-font">Porter №3:</span>
                <span className="column-content-font">{porter3} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.porter3.profit} rub.
                </span>
              </div>
            )}
            {porter4 && (
              <div>
                <span className="column-title-font">Porter №4:</span>
                <span className="column-content-font">{porter4} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.porter4.profit} rub.
                </span>
              </div>
            )}
            {porter5 && (
              <div>
                <span className="column-title-font">Porter №5:</span>
                <span className="column-content-font">{porter5} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.porter5.profit} rub.
                </span>
              </div>
            )}
            {porter6 && (
              <div>
                <span className="column-title-font">Porter №6:</span>
                <span className="column-content-font">{porter6} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.porter6.profit} rub.
                </span>
              </div>
            )}
            {loader && (
              <div>
                <span className="column-title-font">Forklift Operator:</span>
                <span className="column-content-font">{loader} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.loader.profit} rub.
                </span>
              </div>
            )}
            {helperOfTechnologist && (
              <div>
                <span className="column-title-font">
                  Technologist's Assistant:
                </span>
                <span className="column-content-font">
                  {helperOfTechnologist} /
                </span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.helperOfTechnologist.profit} rub.
                </span>
              </div>
            )}
            {grainstoreWorker1 && (
              <div>
                <span className="column-title-font">Grainstore Worker №1:</span>
                <span className="column-content-font">
                  {grainstoreWorker1} /
                </span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.grainstoreWorker1.profit} rub.
                </span>
              </div>
            )}
            {grainstoreWorker2 && (
              <div>
                <span className="column-title-font">Grainstore Worker №2:</span>
                <span className="column-content-font">
                  {grainstoreWorker2} /
                </span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.grainstoreWorker2.profit} rub.
                </span>
              </div>
            )}
            {packer && (
              <div>
                <span className="column-title-font">Packer:</span>
                <span className="column-content-font">{packer} /</span>
                <span
                  className="column-content-font"
                  style={{ color: "green" }}
                >
                  {" "}
                  {workers.packer.profit} rub.
                </span>
              </div>
            )}
          </div>
        )}
        <Table
          dataSource={instances}
          columns={columnsForModal}
          pagination={false}
          size="small"
        />

        <div className="common-modal__info-block">
          <div>
            <span className="column-title-font">Total produced:</span>
            <span className="column-content-font">{totalWeight} kg</span>
          </div>

          <div>
            <span className="column-title-font">Comment:</span>
            <span className="column-content-font">{comment}</span>
          </div>
        </div>
      </Card>
    );
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: (
        <div>
          <FieldNumberOutlined /> <CalendarOutlined />
        </div>
      ),
      dataIndex: "serial",
      key: "serial",
      render: (text, record) => (
        <span className="column-content-font">
          {record.serial} / {record.date} /
          {record.period === "day" ? "day" : " night"}
        </span>
      ),
    },
    {
      title: <span className="column-title-font">Produced:</span>,
      dataIndex: "totalWeight",
      key: "totalWeight",
      render: (text, record) =>
        record.cancelled ? (
          <span className="column-title-font">cancelled</span>
        ) : (
          <span className="column-content-font">{text} kg</span>
        ),
    },
    {
      title: <span className="column-title-font">Actions</span>,
      key: "action",
      render: (text, record) => (
        <Space>
          <Button
            className="common-table-button"
            size="small"
            type="link"
            onClick={() => showModal(record)}
            icon={<EyeOutlined />}
          />
          <Button
            disabled={record.cancelled || record.type === "initialProducts"}
            className="common-table-button"
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditShift(record)}
          />
          <Button
            disabled={record.cancelled || record.type === "initialProducts"}
            className="common-table-button"
            type="link"
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => showConfirmCancel(record)}
            danger
          />
        </Space>
      ),
    },
  ];

  useEffect(() => {
    if (!shifts) {
      setLoading(true);
      return;
    }

    const filteredShifts = filterShifts(filter, shifts, searchValue);

    const dataSource = prepareDataSource(filteredShifts);
    setDataSource(dataSource);

    setLoading(false);
  }, [filter, shifts, searchValue]);

  useEffect(() => {
    if (history.location.state?.listViewState) {
      const { filter, isModalVisible, searchValue } =
        history.location.state.listViewState;
      setFilter(filter);
      setIsModalVisible(isModalVisible);
      setSearchValue(searchValue);
    }
  }, [history.location.state]);

  return (
    <div className="common-container">
      <Spin spinning={loading}>
        <Button type="ghost" onClick={comeback} className="back-route-button">
          <LeftOutlined />
          <h4 className="back-route-button--title">Back</h4>
        </Button>

        <br />
        <h4 className="common-title--large">All Shifts</h4>
        <Input.Search
          value={searchValue}
          placeholder="Search by serial number / worker's last name / first name"
          onChange={handleSearchChange}
          style={{ marginBottom: 8 }}
          size="small"
        />
        <Radio.Group
          onChange={handleFilterChange}
          value={filter}
          style={{ marginBottom: 16 }}
          size="small"
        >
          <Radio.Button value="all">All</Radio.Button>
          <Radio.Button value="dayShift">Day</Radio.Button>
          <Radio.Button value="nightShift">Night</Radio.Button>
          <Radio.Button value="withRepair">With Repair</Radio.Button>
          <Radio.Button value="cancelled">Cancelled</Radio.Button>
        </Radio.Group>

        <Table
          rowClassName={(record) => {
            if (record.cancelled) return "cancelled";
            return "";
          }}
          pagination={{ pageSize: 20 }}
          dataSource={dataSource}
          columns={columns}
        />
        <Modal
          open={isModalVisible}
          bodyStyle={{ marginTop: "20px" }}
          onCancel={handleCancel}
          footer={null}
        >
          <ShiftModalContent />
        </Modal>
      </Spin>
    </div>
  );
};
